import type { ReactElement } from 'react'
import { Link } from '@tanstack/react-router'
import { isNavItemActive, navForProducts } from '../lib/nav.ts'
import type { NavItem, NavProduct } from '../lib/nav.ts'

/**
 * The primary navigation: a sidebar, not a top bar.
 *
 * Nine links already filled the old top bar edge to edge, and the list only
 * grows — Vault alone contributes six. A vertical list has room, keeps the
 * section grouping legible, and does not reflow into a second row the
 * moment someone adds a page.
 *
 * Each section is its own `<nav>` with its own `aria-label` rather than one
 * `<nav>` with headings inside it. Screen reader users navigate by landmark
 * far more than by heading in a list like this, so "Vault navigation" as a
 * landmark beats an `<h2>` — and it keeps the document's heading outline
 * free for the page's single `<h1>`, which is the whole point of the
 * PageHeader primitive.
 */

export interface SidebarNavProps {
  /** Passed in rather than read from the router here so the shell makes one
   * `useRouterState` subscription for the whole chrome, and so this renders
   * in a test without a router state store. */
  pathname: string
  /** The tenant's products, or `null` while unknown — see
   * `navForProducts`, which owns what each of those means. Passed down
   * from the root route's loader rather than fetched here: the sidebar
   * must not be the thing that makes an API call, or every page's chrome
   * would pop its sections in after first paint. */
  products: readonly NavProduct[] | null
}

/**
 * Active styling is a fill, an accent label and a 2px accent bar on the
 * leading edge — three signals, because the brief's gate says status is
 * never carried by colour alone and "which page am I on" is status.
 *
 * `aria-current="page"` is set as well, not instead: the attribute is what
 * a screen reader announces, the bar is what a human sees across the room.
 * The old nav had the attribute and nothing visible.
 */
const ACTIVE_CLASSES =
  'border-l-2 border-accent bg-elevated font-medium text-accent'

const INACTIVE_CLASSES =
  'border-l-2 border-transparent text-muted hover:bg-elevated hover:text-fg'

const ITEM_BASE_CLASSES =
  'flex min-h-row items-center rounded-r-md py-1 pr-2 pl-2.5 text-sm transition-colors'

function NavLink({
  item,
  isActive,
}: {
  item: NavItem
  isActive: boolean
}): ReactElement {
  return (
    <Link
      to={item.to}
      params={item.params}
      aria-current={isActive ? 'page' : undefined}
      className={`${ITEM_BASE_CLASSES} ${isActive ? ACTIVE_CLASSES : INACTIVE_CLASSES}`}
    >
      {item.label}
    </Link>
  )
}

export function SidebarNav({
  pathname,
  products,
}: SidebarNavProps): ReactElement {
  // Sections for THIS tenant. A product the tenant has not bought is not
  // rendered at all rather than rendered disabled: a greyed-out Vault
  // section is an advert, and the operator cannot act on it anyway.
  const sections = navForProducts(products)

  return (
    <aside className="flex w-52 shrink-0 flex-col gap-5 border-r border-subtle bg-surface px-2 py-3">
      {/* The wordmark doubles as the link home. `/` is not in `NAV` on
          purpose — it is the landing page, not a section, and giving it a
          row in the Platform list would imply it is one. */}
      <Link
        to="/"
        className="px-2.5 text-sm font-semibold tracking-tight text-fg transition-colors hover:text-accent"
      >
        Stoaria Control
      </Link>

      {sections.map((section) => (
        <nav key={section.id} aria-label={section.label}>
          {/* Uppercase, tracked out, faint: a section label is orientation,
              not content. `text-faint` is the 3:1 floor token, legal for
              exactly this kind of secondary text and nothing smaller. */}
          <p className="mb-1 px-2.5 text-xs font-medium tracking-wider text-faint uppercase">
            {section.label}
          </p>
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <li key={`${section.id}:${item.to}:${item.label}`}>
                <NavLink
                  item={item}
                  isActive={isNavItemActive(pathname, item)}
                />
              </li>
            ))}
          </ul>
        </nav>
      ))}
    </aside>
  )
}
